import type { Circuit } from '../engine/circuit';
import type { Element } from '../engine/element';
import { SPEC_BY_TYPE, createElement } from '../engine/catalog';
import { Switch } from '../engine/elements';
import { Port, SubcktInstance } from '../engine/subckt';
import { LogicInput } from '../engine/logic';
import { PushButton } from '../engine/mcu';
import type { Renderer } from './render';
import { GRID, elementBounds } from './symbols';
import { deserialize, serialize, type CircuitFile } from './storage';

export type Tool = 'select' | string;

type DragMode = 'none' | 'create' | 'move' | 'pan' | 'rubber' | 'endpoint';

interface DragState {
  mode: DragMode;
  startScreen: { x: number; y: number };
  startGrid: { x: number; y: number };
  lastGrid: { x: number; y: number };
  startView: { ox: number; oy: number };
  origin: Map<string, [number, number, number, number]>;
  endpointIndex: number;
  endpointEl: Element | null;
  moved: boolean;
}

const HIT_BASE = 9;
let HIT_RADIUS = HIT_BASE; // 터치일 때는 더 넓게

/** 캔버스 상호작용(배치/선택/이동/배선) 담당 */
export class Editor {
  tool: Tool = 'select';
  selected = new Set<Element>();
  hover: Element | null = null;
  preview: Element | null = null;
  rubber: { x1: number; y1: number; x2: number; y2: number } | null = null;
  cursorGrid: { x: number; y: number } | null = null;

  onChange: () => void = () => {};
  onSelectionChange: () => void = () => {};
  onToolChange: () => void = () => {};
  /** 모듈 인스턴스를 더블클릭했을 때 */
  onOpenModule: (el: SubcktInstance) => void = () => {};
  /** 'subckt:<정의 id>' 도구로 놓을 인스턴스를 만든다 (main 이 연결) */
  subcktFactory: (defId: string, x1: number, y1: number, x2: number, y2: number) => Element | null = () => null;

  private drag: DragState | null = null;
  private pressedButton: PushButton | null = null;
  /** 터치: 지금 화면에 닿은 손가락들 */
  private pointers = new Map<number, { x: number; y: number }>();
  private pinch: { dist: number; mid: { x: number; y: number }; world: { x: number; y: number }; scale: number } | null = null;
  private lastTap = { time: 0, x: 0, y: 0 };
  private history: CircuitFile[] = [];
  private future: CircuitFile[] = [];
  private spaceDown = false;

  constructor(
    private circuit: Circuit,
    private renderer: Renderer,
  ) {}

  /* ───────── 이벤트 연결 ───────── */

  attach(): void {
    const c = this.renderer.canvas;
    c.addEventListener('pointerdown', this.onPointerDown);
    c.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    c.addEventListener('wheel', this.onWheel, { passive: false });
    c.addEventListener('contextmenu', (e) => e.preventDefault());
    c.addEventListener('dblclick', (e) => {
      const r = c.getBoundingClientRect();
      const w = this.renderer.screenToWorld(e.clientX - r.left, e.clientY - r.top);
      const hit = this.hitTest(w.x, w.y);
      if (hit instanceof SubcktInstance) this.onOpenModule(hit);
    });
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  setTool(tool: Tool): void {
    this.tool = tool;
    this.preview = null;
    if (tool !== 'select') this.clearSelection();
    this.onToolChange();
  }

  /* ───────── 포인터 ───────── */

  private local(e: PointerEvent | WheelEvent): { x: number; y: number } {
    const r = this.renderer.canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private onPointerDown = (e: PointerEvent): void => {
    this.renderer.canvas.setPointerCapture(e.pointerId);
    const s = this.local(e);
    const touch = e.pointerType === 'touch';
    HIT_RADIUS = touch ? HIT_BASE * 1.8 : HIT_BASE;
    this.pointers.set(e.pointerId, s);

    // 두 손가락 = 핀치 줌 / 이동
    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      this.pinch = {
        dist: Math.max(10, Math.hypot(a.x - b.x, a.y - b.y)),
        mid,
        world: this.renderer.screenToWorld(mid.x, mid.y),
        scale: this.renderer.view.scale,
      };
      // 한 손가락으로 하던 일은 취소
      if (this.drag?.mode === 'create') this.preview = null;
      this.drag = null;
      this.rubber = null;
      return;
    }
    if (this.pointers.size > 2) return;

    const g = this.renderer.screenToGrid(s.x, s.y);
    const w = this.renderer.screenToWorld(s.x, s.y);

    // 두 번 톡 = 더블클릭 (모듈 열기)
    if (touch) {
      const now = performance.now();
      const dbl = now - this.lastTap.time < 320 && Math.hypot(s.x - this.lastTap.x, s.y - this.lastTap.y) < 24;
      this.lastTap = { time: dbl ? 0 : now, x: s.x, y: s.y };
      if (dbl && this.tool === 'select') {
        const hit = this.hitTest(w.x, w.y);
        if (hit instanceof SubcktInstance) {
          this.onOpenModule(hit);
          return;
        }
      }
    }

    const base: DragState = {
      mode: 'none',
      startScreen: s,
      startGrid: g,
      lastGrid: g,
      startView: { ox: this.renderer.view.ox, oy: this.renderer.view.oy },
      origin: new Map(),
      endpointIndex: -1,
      endpointEl: null,
      moved: false,
    };

    // 가운데 버튼 / 스페이스 = 화면 이동
    if (e.button === 1 || this.spaceDown) {
      this.drag = { ...base, mode: 'pan' };
      return;
    }

    if (this.tool !== 'select') {
      this.drag = { ...base, mode: 'create' };
      this.preview = this.makePreview(g, g);
      return;
    }

    // 선택 도구
    const hit = this.hitTest(w.x, w.y);

    // 선택된 소자의 끝점 잡기
    if (this.selected.size === 1 && !([...this.selected][0].usesLayout) && [...this.selected][0].pinCount === 2) {
      const el = [...this.selected][0];
      const pins = el.pins;
      for (let i = 0; i < pins.length; i++) {
        const dx = pins[i].x * GRID - w.x;
        const dy = pins[i].y * GRID - w.y;
        if (Math.hypot(dx, dy) < HIT_RADIUS) {
          this.snapshot();
          this.drag = { ...base, mode: 'endpoint', endpointEl: el, endpointIndex: i };
          return;
        }
      }
    }

    if (hit) {
      if (e.shiftKey) {
        if (this.selected.has(hit)) this.selected.delete(hit);
        else this.selected.add(hit);
      } else if (!this.selected.has(hit)) {
        this.selected.clear();
        this.selected.add(hit);
      }
      // 스위치는 클릭으로 바로 켜고 끈다
      if (hit instanceof PushButton && !e.shiftKey) {
        hit.pressed = true;
        this.pressedButton = hit;
        this.circuit.markDirty();
      }
      if ((hit instanceof Switch || hit instanceof LogicInput) && !e.shiftKey) {
        this.snapshot();
        hit.toggle();
        this.circuit.markDirty();
        this.onChange();
      }
      this.onSelectionChange();
      this.snapshot();
      const origin = new Map<string, [number, number, number, number]>();
      for (const el of this.selected) origin.set(el.id, [el.x1, el.y1, el.x2, el.y2]);
      this.drag = { ...base, mode: 'move', origin };
      return;
    }

    if (!e.shiftKey) this.clearSelection();
    if (touch) {
      this.drag = { ...base, mode: 'pan' };
      return;
    }
    this.drag = { ...base, mode: 'rubber' };
    this.rubber = { x1: w.x, y1: w.y, x2: w.x, y2: w.y };
  };

  private onPointerMove = (e: PointerEvent): void => {
    const s = this.local(e);
    if (this.pointers.has(e.pointerId)) this.pointers.set(e.pointerId, s);
    if (this.pinch && this.pointers.size >= 2) {
      const [a, b] = [...this.pointers.values()];
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const dist = Math.max(10, Math.hypot(a.x - b.x, a.y - b.y));
      const v = this.renderer.view;
      v.scale = Math.min(3.5, Math.max(0.3, this.pinch.scale * (dist / this.pinch.dist)));
      // 처음 손가락 가운데 있던 지점이 지금 가운데로 오게
      v.ox = mid.x - this.pinch.world.x * v.scale;
      v.oy = mid.y - this.pinch.world.y * v.scale;
      return;
    }
    const g = this.renderer.screenToGrid(s.x, s.y);
    const w = this.renderer.screenToWorld(s.x, s.y);
    this.cursorGrid = this.tool === 'select' ? null : g;

    if (!this.drag) {
      this.hover = this.hitTest(w.x, w.y);
      return;
    }

    const d = this.drag;
    switch (d.mode) {
      case 'pan':
        this.renderer.view.ox = d.startView.ox + (s.x - d.startScreen.x);
        this.renderer.view.oy = d.startView.oy + (s.y - d.startScreen.y);
        break;

      case 'create':
        this.preview = this.makePreview(d.startGrid, g);
        break;

      case 'move': {
        const dx = g.x - d.startGrid.x;
        const dy = g.y - d.startGrid.y;
        if (dx !== 0 || dy !== 0) d.moved = true;
        for (const el of this.selected) {
          const o = d.origin.get(el.id);
          if (!o) continue;
          el.x1 = o[0] + dx;
          el.y1 = o[1] + dy;
          el.x2 = o[2] + dx;
          el.y2 = o[3] + dy;
        }
        this.circuit.markDirty();
        break;
      }

      case 'endpoint': {
        const el = d.endpointEl;
        if (!el) break;
        if (d.endpointIndex === 0) {
          el.x1 = g.x;
          el.y1 = g.y;
        } else {
          el.x2 = g.x;
          el.y2 = g.y;
        }
        d.moved = true;
        this.circuit.markDirty();
        break;
      }

      case 'rubber':
        if (this.rubber) {
          this.rubber.x2 = w.x;
          this.rubber.y2 = w.y;
        }
        break;
    }
  };

  private onPointerUp = (e?: PointerEvent): void => {
    if (e) this.pointers.delete(e.pointerId);
    if (this.pinch) {
      if (this.pointers.size < 2) this.pinch = null;
      return;
    }
    if (this.pressedButton) {
      this.pressedButton.pressed = false;
      this.pressedButton = null;
      this.circuit.markDirty();
    }
    const d = this.drag;
    this.drag = null;
    if (!d) return;

    if (d.mode === 'create' && this.preview) {
      const el = this.preview;
      this.preview = null;
      if (el.pinCount === 2 && el.x1 === el.x2 && el.y1 === el.y2) {
        const spec = SPEC_BY_TYPE[el.type];
        el.x2 = el.x1 + (spec?.defaultLength ?? 2);
      }
      this.addElement(el);
      this.selected.clear();
      this.selected.add(el);
      this.onSelectionChange();
    }

    if ((d.mode === 'move' || d.mode === 'endpoint') && d.moved) {
      for (const el of this.selected) for (const p of el.pins) this.splitWiresAtPoint(p.x, p.y, el);
      if (d.endpointEl) for (const p of d.endpointEl.pins) this.splitWiresAtPoint(p.x, p.y, d.endpointEl);
      this.circuit.markDirty();
      this.onChange();
    }

    if (d.mode === 'rubber' && this.rubber) {
      const r = this.rubber;
      const x1 = Math.min(r.x1, r.x2);
      const x2 = Math.max(r.x1, r.x2);
      const y1 = Math.min(r.y1, r.y2);
      const y2 = Math.max(r.y1, r.y2);
      if (Math.abs(x2 - x1) > 3 || Math.abs(y2 - y1) > 3) {
        for (const el of this.circuit.elements) {
          const inside = el.pins.every((p) => {
            const px = p.x * GRID;
            const py = p.y * GRID;
            return px >= x1 && px <= x2 && py >= y1 && py <= y2;
          });
          if (inside) this.selected.add(el);
        }
        this.onSelectionChange();
      }
      this.rubber = null;
    }
  };

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const s = this.local(e);
    this.renderer.zoomAt(s.x, s.y, e.deltaY < 0 ? 1.12 : 1 / 1.12);
  };

  /* ───────── 키보드 ───────── */

  private onKeyDown = (e: KeyboardEvent): void => {
    const target = e.target as HTMLElement | null;
    if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

    if (e.code === 'Space') {
      this.spaceDown = true;
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      this.deleteSelected();
    } else if (e.key === 'Escape') {
      this.setTool('select');
      this.clearSelection();
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) this.redo();
      else this.undo();
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
      e.preventDefault();
      this.duplicateSelected();
    } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      this.selected = new Set(this.circuit.elements);
      this.onSelectionChange();
    } else if (e.key.toLowerCase() === 'r') {
      this.rotateSelected();
    } else if (e.key.toLowerCase() === 'f') {
      this.flipSelected();
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (e.code === 'Space') this.spaceDown = false;
  };

  /* ───────── 조작 ───────── */

  private makePreview(a: { x: number; y: number }, b: { x: number; y: number }): Element {
    if (this.tool.startsWith('subckt:')) {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      let ux = 1;
      let uy = 0;
      if (dx !== 0 || dy !== 0) {
        if (Math.abs(dx) >= Math.abs(dy)) ux = Math.sign(dx);
        else {
          ux = 0;
          uy = Math.sign(dy);
        }
      }
      const L = SPEC_BY_TYPE.subckt.defaultLength;
      const el = this.subcktFactory(this.tool.slice(7), a.x, a.y, a.x + ux * L, a.y + uy * L);
      if (el) return el;
    }
    const spec = SPEC_BY_TYPE[this.tool];
    if (!spec) return createElement('wire', a.x, a.y, b.x, b.y);
    if (spec.pinCount === 1) {
      if (this.tool === 'logicin' || this.tool === 'clock') return createElement(this.tool, b.x, b.y, b.x - 1, b.y);
      if (this.tool === 'logicout') return createElement(this.tool, b.x, b.y, b.x + 1, b.y);
      return createElement(this.tool, b.x, b.y, b.x, b.y + 1);
    }
    if (spec.pinCount > 2) {
      // 다핀 소자: 드래그 방향만 보고 길이는 고정
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      let ux = 1;
      let uy = 0;
      if (dx !== 0 || dy !== 0) {
        if (Math.abs(dx) >= Math.abs(dy)) ux = Math.sign(dx);
        else {
          ux = 0;
          uy = Math.sign(dy);
        }
      }
      const L = spec.defaultLength;
      return createElement(this.tool, a.x, a.y, a.x + ux * L, a.y + uy * L);
    }
    // 축에 맞춰 직선으로 스냅 (대각선은 shift 없이도 허용)
    let ex = b.x;
    let ey = b.y;
    const dx = Math.abs(b.x - a.x);
    const dy = Math.abs(b.y - a.y);
    if (dx >= dy) ey = a.y;
    else ex = a.x;
    return createElement(this.tool, a.x, a.y, ex, ey);
  }

  addElement(el: Element): void {
    this.snapshot();
    if (el instanceof Port) {
      // 포트 이름 자동: P1, P2 … (겹치지 않게)
      const used = new Set(this.circuit.elements.filter((e) => e instanceof Port).map((e) => (e as Port).portName));
      let i = 1;
      while (used.has(`P${i}`)) i++;
      if (!el.portName || used.has(el.portName)) el.props.name = `P${i}`;
    }
    const pieces = el.type === 'wire' ? this.splitNewWire(el) : [el];
    for (const piece of pieces) this.circuit.add(piece);
    for (const piece of pieces) for (const p of piece.pins) this.splitWiresAtPoint(p.x, p.y, piece);
    this.circuit.markDirty();
    this.onChange();
  }

  deleteSelected(): void {
    if (this.selected.size === 0) return;
    this.snapshot();
    for (const el of this.selected) this.circuit.remove(el);
    this.clearSelection();
    this.circuit.markDirty();
    this.onChange();
  }

  duplicateSelected(): void {
    if (this.selected.size === 0) return;
    this.snapshot();
    const copies: Element[] = [];
    for (const el of this.selected) {
      copies.push(createElement(el.type, el.x1 + 1, el.y1 + 1, el.x2 + 1, el.y2 + 1, { ...el.props }));
    }
    for (const c of copies) this.circuit.add(c);
    this.selected = new Set(copies);
    this.circuit.markDirty();
    this.onChange();
    this.onSelectionChange();
  }

  rotateSelected(): void {
    if (this.selected.size === 0) return;
    this.snapshot();
    let sx = 0;
    let sy = 0;
    let n = 0;
    for (const el of this.selected) {
      sx += el.x1 + el.x2;
      sy += el.y1 + el.y2;
      n += 2;
    }
    const cx = Math.round(sx / n);
    const cy = Math.round(sy / n);
    const rot = (x: number, y: number): [number, number] => [cx - (y - cy), cy + (x - cx)];
    for (const el of this.selected) {
      const [nx1, ny1] = rot(el.x1, el.y1);
      const [nx2, ny2] = rot(el.x2, el.y2);
      el.x1 = nx1; el.y1 = ny1; el.x2 = nx2; el.y2 = ny2;
    }
    this.circuit.markDirty();
    this.onChange();
  }

  /** 3핀 이상 소자 좌우(위아래) 반전 — 예: 트랜지스터의 C/E 위치 바꾸기 */
  flipSelected(): void {
    const targets = [...this.selected].filter((el) => el.usesLayout);
    if (targets.length === 0) return;
    this.snapshot();
    for (const el of targets) el.props.flip = !el.bool('flip', false);
    this.circuit.markDirty();
    this.onChange();
    this.onSelectionChange();
  }

  clearSelection(): void {
    if (this.selected.size === 0) return;
    this.selected.clear();
    this.onSelectionChange();
  }

  /* ───────── 도선 자동 분할 ───────── */

  /** (x,y) 가 기존 도선의 중간에 닿으면 그 도선을 두 개로 쪼갠다 */
  private splitWiresAtPoint(x: number, y: number, except: Element): void {
    for (const w of [...this.circuit.elements]) {
      if (w === except || w.type !== 'wire') continue;
      if (!isStrictlyInside(x, y, w.x1, w.y1, w.x2, w.y2)) continue;
      const a = createElement('wire', w.x1, w.y1, x, y);
      const b = createElement('wire', x, y, w.x2, w.y2);
      this.circuit.remove(w);
      this.circuit.add(a);
      this.circuit.add(b);
    }
  }

  /** 새로 그린 도선 위에 기존 핀이 있으면 여러 조각으로 나눠 반환 */
  private splitNewWire(wire: Element): Element[] {
    const inner: { x: number; y: number; t: number }[] = [];
    const seen = new Set<string>();
    for (const el of this.circuit.elements) {
      for (const p of el.pins) {
        const k = `${p.x},${p.y}`;
        if (seen.has(k)) continue;
        if (!isStrictlyInside(p.x, p.y, wire.x1, wire.y1, wire.x2, wire.y2)) continue;
        seen.add(k);
        inner.push({ x: p.x, y: p.y, t: Math.hypot(p.x - wire.x1, p.y - wire.y1) });
      }
    }
    if (inner.length === 0) return [wire];
    inner.sort((a, b) => a.t - b.t);
    const pts = [{ x: wire.x1, y: wire.y1 }, ...inner, { x: wire.x2, y: wire.y2 }];
    const out: Element[] = [];
    for (let i = 0; i < pts.length - 1; i++) {
      out.push(createElement('wire', pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y));
    }
    return out;
  }

  /* ───────── 히트 테스트 ───────── */

  hitTest(wx: number, wy: number): Element | null {
    // 부품을 도선보다 먼저 (부품 끝에 도선이 붙어 있어도 부품이 잡히게)
    return this.hitPass(wx, wy, false) ?? this.hitPass(wx, wy, true);
  }

  private hitPass(wx: number, wy: number, wires: boolean): Element | null {
    for (let i = this.circuit.elements.length - 1; i >= 0; i--) {
      const el = this.circuit.elements[i];
      if ((el.type === 'wire') !== wires) continue;
      if (el instanceof Port) {
        const dir = el.side === 'left' ? -1 : 1;
        const px = el.x1 * GRID;
        const py = el.y1 * GRID;
        const lo = dir < 0 ? px - 50 : px - 5;
        const hi = dir < 0 ? px + 5 : px + 50;
        if (wx >= lo && wx <= hi && Math.abs(wy - py) < 9) return el;
        continue;
      }
      if (el.usesLayout) {
        const b = elementBounds(el);
        if (wx >= b.x1 && wx <= b.x2 && wy >= b.y1 && wy <= b.y2) return el;
        continue;
      }
      if (el.pinCount === 1 && !el.usesLayout) {
        // 핀에서 몸통(x2,y2) 쪽까지
        if (distToSegment(wx, wy, el.x1 * GRID, el.y1 * GRID, el.x2 * GRID, el.y2 * GRID) < HIT_RADIUS + 5) return el;
        continue;
      }
      if (distToSegment(wx, wy, el.x1 * GRID, el.y1 * GRID, el.x2 * GRID, el.y2 * GRID) < HIT_RADIUS) return el;
    }
    return null;
  }

  /* ───────── 되돌리기 ───────── */

  snapshot(): void {
    const snap = serialize(this.circuit, 'undo');
    const last = this.history[this.history.length - 1];
    if (last && JSON.stringify(last.elements) === JSON.stringify(snap.elements)) return;
    this.history.push(snap);
    if (this.history.length > 80) this.history.shift();
    this.future = [];
  }

  undo(): void {
    const prev = this.history.pop();
    if (!prev) return;
    this.future.push(serialize(this.circuit, 'redo'));
    this.restore(prev);
  }

  redo(): void {
    const next = this.future.pop();
    if (!next) return;
    this.history.push(serialize(this.circuit, 'undo'));
    this.restore(next);
  }

  private restore(file: CircuitFile): void {
    for (const d of file.subcircuits ?? []) if (!this.circuit.defs.has(d.id)) this.circuit.defs.set(d.id, d);
    this.circuit.elements = deserialize(file);
    this.selected.clear();
    this.circuit.markDirty();
    this.circuit.reset();
    this.onChange();
    this.onSelectionChange();
  }

  get canUndo(): boolean {
    return this.history.length > 0;
  }
  get canRedo(): boolean {
    return this.future.length > 0;
  }
  resetHistory(): void {
    this.history = [];
    this.future = [];
  }
}

/* ───────── 기하 도우미 ───────── */

function distToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/** 격자점 (x,y) 가 선분 위에 있고 양 끝점은 아닌지 */
function isStrictlyInside(x: number, y: number, x1: number, y1: number, x2: number, y2: number): boolean {
  if ((x === x1 && y === y1) || (x === x2 && y === y2)) return false;
  const cross = (x - x1) * (y2 - y1) - (y - y1) * (x2 - x1);
  if (cross !== 0) return false;
  return x >= Math.min(x1, x2) && x <= Math.max(x1, x2) && y >= Math.min(y1, y2) && y <= Math.max(y1, y2);
}
